import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import Ripple from '@/components/Ripple'
import { useUserInfo } from '@/hooks'
import { toFixedNoRound } from '@/utils'
import { Toast } from 'react-vant'
import CurrencySelectPopup, { type Currency as PopupCurrency } from '@/components/CurrencySelectPopup'
import { APIWeb } from '@/api'
import { useTransactionRefresh } from '@/hooks'

// 缓存 key
const REMITTANCE_FROM_CURRENCY_KEY = 'remittance_from_currency'

// 币种类型
type CurrencyType = 'USDT' | 'USDC' | 'USD1'

interface Currency {
  name: CurrencyType
  icon: string
  balance: number
}

export default React.memo(function Remittance() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { userInfo } = useUserInfo()
  const { refreshAfterTransaction } = useTransactionRefresh()

  // 获取币种图标
  const getCurrencyIcon = useCallback((currency: string) => {
    const currencyMap: Record<string, string> = {
      'USDT': '/images/home/usdt.png',
      'USDC': '/images/home/usdc.png',
      'USD1': '/images/home/usd1.png',
      'USD': '/images/home/usd.png',
    }
    return currencyMap[currency.toUpperCase()] || '/images/home/usdt.png'
  }, [])

  // 汇出币种 - 使用缓存或默认值
  const [fromCurrency, setFromCurrency] = useState<CurrencyType>(() => {
    const cached = localStorage.getItem(REMITTANCE_FROM_CURRENCY_KEY)
    return (cached as CurrencyType) || 'USDT'
  })
  const [fromAmount, setFromAmount] = useState('')

  // 实际汇出金额（手续费等计算后的实际金额）
  const [actualAmount, setActualAmount] = useState('')

  // 收款人
  const [selectedPayee, setSelectedPayee] = useState<ModelsPaymentAccount | null>(null)

  // 提交加载状态
  const [loading, setLoading] = useState(false)

  // Popup 控制状态
  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false)
  const [currencySelectType, setCurrencySelectType] = useState<'from' | 'to' | null>(null)


  // 币种列表
  const currencies = useMemo<Currency[]>(() => [
    { name: 'USDT', icon: '/images/home/usdt.png', balance: userInfo?.usdt || 0 },
    { name: 'USDC', icon: '/images/home/usdc.png', balance: userInfo?.usdc || 0 },
    { name: 'USD1', icon: '/images/home/usd1.png', balance: userInfo?.usd1 || 0 },
  ], [userInfo])

  // 获取当前币种信息
  const fromCurrencyInfo = useMemo(() =>
    currencies.find(c => c.name === fromCurrency),
    [currencies, fromCurrency]
  )

  // 实际汇出金额计算（这里可以根据手续费等计算）
  const calculatedActualAmount = useMemo(() => {
    if (!fromAmount) return ''
    // TODO: 根据实际业务逻辑计算手续费等
    // 目前暂时等于汇出金额
    return fromAmount
  }, [fromAmount])

  // Popup 币种列表
  const popupCurrencies = useMemo<PopupCurrency[]>(() => [
    {
      icon: '/images/home/usdt.png',
      name: 'USDT',
      description: 'Tether USD',
      symbol: 'USDT'
    },
    {
      icon: '/images/home/usdc.png',
      name: 'USDC',
      description: 'USD Coin',
      symbol: 'USDC'
    },
    {
      icon: '/images/home/usd1.png',
      name: 'USD1',
      description: 'USD1',
      symbol: 'USD1'
    }
  ], [])

  // 监听币种变化，保存到缓存
  useEffect(() => {
    localStorage.setItem(REMITTANCE_FROM_CURRENCY_KEY, fromCurrency)
  }, [fromCurrency])

  // 同步实际汇出金额
  useEffect(() => {
    setActualAmount(calculatedActualAmount)
  }, [calculatedActualAmount])

  // 接收从收款人列表页面传递的数据
  useEffect(() => {
    const state = location.state as { selectedPayee?: ModelsPaymentAccount }
    if (state?.selectedPayee) {
      setSelectedPayee(state.selectedPayee)
      // 清除 location.state，防止重复设置
      window.history.replaceState({}, '')
    }
  }, [location.state])

  // 设置最大金额
  const handleMaxFrom = useCallback(() => {
    if (fromCurrencyInfo) {
      setFromAmount(toFixedNoRound(fromCurrencyInfo.balance, 2))
    }
  }, [fromCurrencyInfo])

  // 移除 handleMaxTo

  // 选择汇出币种
  const handleSelectFromCurrency = useCallback(() => {
    setCurrencySelectType('from')
    setShowCurrencyPopup(true)
  }, [])

  // 处理币种选择
  const handleCurrencySelect = useCallback((currency: PopupCurrency) => {
    const symbol = currency.symbol as CurrencyType
    if (currencySelectType === 'from') {
      setFromCurrency(symbol)
    }
  }, [currencySelectType])

  // 添加或选择收款人
  const handleAddPayee = useCallback(() => {
    navigate('/remittance/payee-list', {
      state: {
        selectMode: true  // 标识从汇款页面进入，用于返回时的路由判断
      }
    })
  }, [navigate])

  // 查看汇款记录
  const handleViewRecord = useCallback(() => {
    navigate('/remittance/record')
  }, [navigate])

  // 检查表单是否有效
  const isFormValid = useMemo(() => {
    return (
      selectedPayee &&
      fromAmount &&
      parseFloat(fromAmount) > 0
    )
  }, [selectedPayee, fromAmount])

  // 提交汇款
  const handleSubmit = useCallback(async () => {
    if (loading) return

    if (!selectedPayee) {
      Toast.info(t('请选择收款人'))
      return
    }
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      Toast.info(t('请输入汇出金额'))
      return
    }

    try {
      setLoading(true)

      // 构造接口参数
      const payTypeMap: Record<CurrencyType, string> = {
        'USDT': 'usdt',
        'USDC': 'usdc',
        'USD1': 'usd1'
      }

      const params: RemittanceRequest = {
        amount: parseFloat(fromAmount),
        pay_type: payTypeMap[fromCurrency] as any,
        payment_account_id: selectedPayee.id!,
        remark: '' // 可以添加备注字段
      }

      await APIWeb.remittance(params)

      Toast.success(t('汇款成功'))
      // 交易成功后刷新状态
      await refreshAfterTransaction()
      // 清空表单
      setFromAmount('')
      setActualAmount('')
      setSelectedPayee(null)
      // 返回上一页
      navigate(-1)
    } catch (error) {
      console.error('汇款失败:', error)
      // Toast 会自动显示错误信息
    } finally {
      setLoading(false)
    }
  }, [selectedPayee, fromAmount, fromCurrency, loading, navigate, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('汇款')}</span>}
        rightChildren={
          <div className='flex items-center cursor-pointer' onClick={handleViewRecord}>
            <div className='w-6 h-6'>
              <img src="/icon/record.svg" alt="record" />
            </div>
          </div>
        }
      />

      <div className="bg-white px-6 py-6">
        {/* 汇出部分 */}
        <div className="mb-6">
          <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('汇出')}</div>

          {/* 币种选择和金额 */}
          <div className="flex min-h-16.5 items-center justify-between bg-white rounded-2xl p-4 border-[0.5px] border-solid border-[#E9E9E9]">
            <div className="flex items-center flex-shrink-0" onClick={handleSelectFromCurrency}>
              <img src={fromCurrencyInfo?.icon} alt={fromCurrency} className="w-5 h-5 mr-3" />
              <div className="flex items-center">
                <span className="text-base font-bold text-black mr-2">{fromCurrency}</span>
                <img src="/icon/picker.svg" alt="" className="w-3" />
              </div>
            </div>

            <div className="flex items-center justify-end flex-1 min-w-0">
              <input
                type="number"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                placeholder="0.00"
                className="text-right text-5 font-semibold text-black placeholder-[#1A1A1A]/60 outline-none flex-1 min-w-0"
              />
              <Ripple className="ml-2 rounded-lg flex-shrink-0">
                <div
                  onClick={handleMaxFrom}
                  className="px-3 py-1 bg-white text-3 rounded-lg border-[0.5px] border-solid border-[#E9E9E9]"
                >
                  {t('最大')}
                </div>
              </Ripple>
            </div>
          </div>

          {/* 可用余额 */}
          <div className="text-3 text-[#979797] mt-2">
            {t('可用余额')}: <span className='text-black ml-2'>{toFixedNoRound(fromCurrencyInfo?.balance || 0, 2)} {fromCurrency}</span>
          </div>
        </div>

        {/* 收款人 */}
        <div className="mb-6">
          <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('收款人')}</div>

          <Ripple className="rounded-2xl">
            <div
              onClick={handleAddPayee}
              className="flex min-h-16.5 items-center justify-between bg-white border-[0.5px] border-solid border-[#E9E9E9] rounded-2xl p-4"
            >
              {selectedPayee ? (
                // 显示已选择的收款人信息
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-black">{selectedPayee.bank_name}</span>

                    <div className="flex items-center gap-2 text-4 font-medium px-2 py-1 rounded">
                      <img
                        src={getCurrencyIcon(selectedPayee.account_ccy || 'USD')}
                        alt={selectedPayee.account_ccy}
                        className="w-6 h-6"
                      />
                      <span>{selectedPayee.account_ccy}</span>
                    </div>
                  </div>
                  <div className="text-xs text-[#0F0F0F]/60 mb-1">
                    {t('账户名')}: {selectedPayee.account_name}
                  </div>
                  <div className="text-xs text-[#0F0F0F]/60 mb-1">
                    {t('银行账户')}: {selectedPayee.bank_account}
                  </div>
                  <div className="text-xs text-[#0F0F0F]/60 mb-1">
                    {t('银行所在国家或地区')}: {selectedPayee.bank_country}
                  </div>
                  <div className="text-xs text-[#0F0F0F]/60">
                    {t('账户币种')}: {selectedPayee.account_ccy}
                  </div>
                </div>
              ) : (
                // 未选择时显示提示
                <div className="w-full flex justify-between items-center text-[var(--primary-color)]">
                  <div className="flex items-center">
                    <div className='w-5 h-5 bg-#CED2ED rounded-1 mr-3 flex items-center justify-center'>
                      <img src="/icon/blue-add.svg" alt="" />
                    </div>
                    <span className="text-xs text-black/50 font-bold">{t('添加或选择收款人')}</span>
                  </div>
                  <div className="ml-3">
                    <img src="/icon/address-book.svg" alt="" />
                  </div>
                </div>

              )}

            </div>
          </Ripple>
        </div>

        {/* 实际汇出 */}
        <div className="mb-6">
          <div className="text-3.5 text-[#0F0F0F]/60 mb-3">{t('实际汇出')}</div>

          {/* 显示实际汇出金额（币种跟随汇出币种） */}
          <div className="flex min-h-16.5 items-center justify-between bg-white border-[0.5px] border-solid border-[#E9E9E9] rounded-2xl p-4">
            <div className="flex items-center flex-shrink-0">
              {/* <img src={fromCurrencyInfo?.icon} alt={fromCurrency} className="w-5 h-5 mr-3" />
              <span className="text-base font-bold text-black">{fromCurrency}</span> */}
              <img src='/images/home/usd.png' alt='USD' className="w-5 h-5 mr-3" />
              <span className="text-base font-bold text-black">USD</span>
            </div>

            <div className="flex items-center justify-end flex-1 min-w-0">
              <input
                type="text"
                value={actualAmount}
                readOnly
                placeholder="0.00"
                className="text-right text-5 font-semibold text-black placeholder-[#1A1A1A]/60 outline-none flex-1 min-w-0 bg-transparent"
              />
            </div>
          </div>
        </div>

        {/* 提交按钮 */}
        <div className="mt-8">
          <CustomButton
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            loading={loading}
          >
            {loading ? t('提交中') : t('提交')}
          </CustomButton>
        </div>
      </div>

      {/* 币种选择 Popup */}
      <CurrencySelectPopup
        visible={showCurrencyPopup}
        onClose={() => {
          setShowCurrencyPopup(false)
          setCurrencySelectType(null)
        }}
        onSelect={handleCurrencySelect}
        currencies={popupCurrencies}
      />
    </PageLayout>
  )
})
